import {FC, useEffect, useRef, useState} from "react";
import {Icon, Popup} from "semantic-ui-react";
import {
    IDriveThruCarEvent,
    IDriveThruTransaction
} from "../api/driveThruReplayModels";
import {CascadeInfo, CascadeCategory} from "../api/driveThruReplayModels";
import {
    getOrderPreview,
    getPaymentDescription,
    getPurchasedItems
} from "../driveThruReplayUtils";
import {
    formatDuration,
    formatHour
} from "@/features/coachInsights/api/coachInsightsUtils";
import dataFormatUtil from "@/util/dataFormatUtil";

// ─── Complexity Scoring ───────────────────────────────────────────────────────

enum ComplexityLevel {
    Simple = "simple",
    Moderate = "moderate",
    Complex = "complex"
}

// Payment variant used to colour the payment chip.
enum PaymentKind {
    Cash = "cash",
    Card = "card",
    Mobile = "mobile"
}

// Single toggle controlling which cars appear in the timeline. Cascades always
// occur within the occurred window, so these three modes are progressively
// broader subsets of the same data.
enum TimelineViewMode {
    Cascades = "cascades",
    OccurredWindow = "occurredWindow",
    AllCars = "allCars"
}

const COMPLEXITY_CONFIG: Record<ComplexityLevel, {dots: number; label: string}> = {
    [ComplexityLevel.Simple]: {dots: 1, label: "Simple"},
    [ComplexityLevel.Moderate]: {dots: 2, label: "Moderate"},
    [ComplexityLevel.Complex]: {dots: 3, label: "Complex"}
};

const COMPLEXITY_TOOLTIP = (
    <>
        Order complexity is based on total item count:
        <br />
        • Simple: 1-4 items
        <br />
        • Moderate: 5-9 items
        <br />• Complex: 10+ items
    </>
);

// Label for the cascade expand/collapse button.
function getCascadeExpandLabel(cascadeLength: number, isExpanded: boolean): string {
    // cascadeLength includes the trigger car, so the number of affected cars is (cascadeLength - 1).
    const affectedCount = cascadeLength - 1;
    if (isExpanded) return `▲ Hide ${affectedCount} affected`;
    const carNoun = affectedCount === 1 ? "car" : "cars";
    return `▼ ${affectedCount} affected ${carNoun}`;
}

function computeComplexity(
    transaction: IDriveThruTransaction | undefined
): ComplexityLevel | null {
    const items = getPurchasedItems(transaction);
    if (items.length === 0) return null;

    const totalItems = items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);

    if (totalItems <= 4) return ComplexityLevel.Simple;
    if (totalItems <= 9) return ComplexityLevel.Moderate;
    return ComplexityLevel.Complex;
}

interface IProps {
    events: IDriveThruCarEvent[];
    selectedEventId: string | null;
    onSelectEvent: (eventId: string) => void;
    thresholdSeconds: number;
    transactions: Record<string, IDriveThruTransaction>;
    cascadeMap: Map<string, CascadeInfo>;
    peakWindowHours?: {start: number; end: number};
}

const DriveThruTimeline: FC<IProps> = ({
    events,
    selectedEventId,
    onSelectEvent,
    thresholdSeconds,
    transactions,
    cascadeMap,
    peakWindowHours
}) => {
    const selectedItemRef = useRef<HTMLDivElement | null>(null);
    const cascadeGroupCount = new Set([...cascadeMap.values()].map((c) => c.groupId))
        .size;

    const defaultViewMode =
        cascadeGroupCount > 0
            ? TimelineViewMode.Cascades
            : peakWindowHours
            ? TimelineViewMode.OccurredWindow
            : TimelineViewMode.AllCars;
    const [viewMode, setViewMode] = useState<TimelineViewMode>(defaultViewMode);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

    const peakFilteredEvents =
        peakWindowHours && viewMode !== TimelineViewMode.AllCars
            ? events.filter((e) => {
                  const hourOfDay = Math.floor(e.secondOfDay / 3600);
                  return (
                      hourOfDay >= peakWindowHours.start &&
                      hourOfDay < peakWindowHours.end
                  );
              })
            : events;

    const toggleGroup = (groupId: number) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const showCascadesOnly = viewMode === TimelineViewMode.Cascades;
    const visibleEvents = showCascadesOnly
        ? peakFilteredEvents.filter((e) => {
              const c = cascadeMap.get(e.id);
              if (!c) return false;
              // always show the trigger; only show affected when its group is expanded
              return (
                  c.category === CascadeCategory.Cascade ||
                  expandedGroups.has(c.groupId)
              );
          })
        : peakFilteredEvents;
    const hiddenCount = peakFilteredEvents.length - visibleEvents.length;
    const allDayHiddenCount = events.length - peakFilteredEvents.length;

    useEffect(() => {
        if (!selectedEventId || visibleEvents.length === 0) return;

        // Keep the selected car in context when the toggle changes the visible set.
        const selectedEventIsVisible = visibleEvents.some(
            (event) => event.id === selectedEventId
        );
        if (selectedEventIsVisible) return;

        const selectedEvent = events.find((event) => event.id === selectedEventId);
        const closestVisibleEvent = selectedEvent
            ? visibleEvents.reduce((closestEvent, event) => {
                  const currentDistance = Math.abs(
                      event.secondOfDay - selectedEvent.secondOfDay
                  );
                  const closestDistance = Math.abs(
                      closestEvent.secondOfDay - selectedEvent.secondOfDay
                  );

                  return currentDistance < closestDistance ? event : closestEvent;
              })
            : visibleEvents[0];

        onSelectEvent(closestVisibleEvent.id);
    }, [events, onSelectEvent, selectedEventId, visibleEvents]);

    useEffect(() => {
        if (!selectedEventId || !selectedItemRef.current) return;

        selectedItemRef.current.scrollIntoView({block: "nearest"});
    }, [selectedEventId, viewMode, expandedGroups]);

    return (
        <div className="drive-thru-replay-timeline-panel">
            <div className="drive-thru-replay-timeline-header">
                <span>Drive-Thru Timeline</span>
                <div className="drive-thru-replay-timeline-legend">
                    <div className="drive-thru-replay-view-toggle">
                        {cascadeGroupCount > 0 && (
                            <button
                                className={`drive-thru-replay-view-toggle-opt${
                                    viewMode === TimelineViewMode.Cascades
                                        ? " active"
                                        : ""
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setViewMode(TimelineViewMode.Cascades);
                                }}
                            >
                                {cascadeGroupCount} cascade
                                {cascadeGroupCount > 1 ? "s" : ""}
                            </button>
                        )}
                        {peakWindowHours && (
                            <button
                                className={`drive-thru-replay-view-toggle-opt${
                                    viewMode === TimelineViewMode.OccurredWindow
                                        ? " active"
                                        : ""
                                }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setViewMode(TimelineViewMode.OccurredWindow);
                                }}
                            >
                                {formatHour(peakWindowHours.start)}-
                                {formatHour(peakWindowHours.end)}
                            </button>
                        )}
                        <button
                            className={`drive-thru-replay-view-toggle-opt${
                                viewMode === TimelineViewMode.AllCars
                                    ? " active"
                                    : ""
                            }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewMode(TimelineViewMode.AllCars);
                            }}
                        >
                            All Day
                        </button>
                    </div>
                    <div className="drive-thru-replay-legend-items">
                        <div className="drive-thru-replay-legend-item">
                            <Icon
                                name="check circle"
                                className="drive-thru-replay-legend-icon ok"
                            />
                            Under {Math.floor(thresholdSeconds / 60)}:
                            {String(thresholdSeconds % 60).padStart(2, "0")}
                        </div>
                        <div className="drive-thru-replay-legend-item">
                            <Icon
                                name="exclamation triangle"
                                className="drive-thru-replay-legend-icon over"
                            />
                            Over threshold
                        </div>
                    </div>
                </div>
            </div>
            <div className="drive-thru-replay-timeline-list">
                {visibleEvents.map((event) => {
                    const isSelected = event.id === selectedEventId;
                    const orderPreview = getOrderPreview(
                        transactions[event.transactionId]
                    );
                    const complexity = computeComplexity(
                        transactions[event.transactionId]
                    );
                    const complexityConfig = complexity
                        ? COMPLEXITY_CONFIG[complexity]
                        : null;
                    const cascade = cascadeMap.get(event.id);
                    const paymentMethod = getPaymentDescription(
                        transactions[event.transactionId]
                    );
                    const isCash = paymentMethod?.toLowerCase() === "cash";
                    const paymentLabel = paymentMethod ?? "";
                    const paymentMod: PaymentKind = isCash
                        ? PaymentKind.Cash
                        : paymentMethod?.toLowerCase().includes("mobile")
                        ? PaymentKind.Mobile
                        : PaymentKind.Card;
                    const itemClass = [
                        "drive-thru-replay-timeline-item",
                        isSelected ? "selected" : ""
                    ]
                        .filter(Boolean)
                        .join(" ");

                    return (
                        <div
                            key={event.id}
                            ref={isSelected ? selectedItemRef : undefined}
                            className={itemClass}
                            data-cascade-role={cascade?.category}
                            onClick={() => onSelectEvent(event.id)}
                        >
                            {cascade && (
                                <div
                                    className="drive-thru-replay-cascade-connector"
                                    data-connector-above={cascade.positionIndex > 0}
                                    data-connector-below={
                                        cascade.positionIndex <
                                        cascade.cascadeLength - 1
                                    }
                                />
                            )}
                            <div className="drive-thru-replay-car-info">
                                <div className="drive-thru-replay-car-label">
                                    Car #{event.vehicleNumber}
                                </div>
                                <div className="drive-thru-replay-car-time">
                                    {dataFormatUtil.formatToTimeOfDayDisplayType(
                                        Math.round(event.secondOfDay / 60)
                                    )}{" "}
                                    - {event.cashierName}
                                    {paymentLabel && (
                                        <span
                                            className="drive-thru-replay-payment-chip"
                                            data-payment={paymentMod}
                                        >
                                            {paymentLabel}
                                        </span>
                                    )}
                                </div>
                                {orderPreview && (
                                    <div className="drive-thru-replay-car-order-preview">
                                        {orderPreview}
                                    </div>
                                )}
                                {complexityConfig && (
                                    <div
                                        className="drive-thru-replay-complexity"
                                        data-complexity={complexity}
                                    >
                                        <span className="drive-thru-replay-complexity-label">
                                            {complexityConfig.label}
                                        </span>
                                        <Popup
                                            trigger={
                                                <Icon
                                                    name="help circle"
                                                    className="drive-thru-replay-complexity-info"
                                                    tabIndex={0}
                                                    aria-label="How order complexity is calculated"
                                                />
                                            }
                                            content={COMPLEXITY_TOOLTIP}
                                            position="top center"
                                            on={["hover", "focus"]}
                                        />
                                    </div>
                                )}
                                {cascade && (
                                    <div
                                        className="drive-thru-replay-cascade-badge"
                                        data-cascade-role={cascade.category}
                                    >
                                        {cascade.category ===
                                        CascadeCategory.Cascade ? (
                                            <span className="drive-thru-replay-cascade-badge-content">
                                                <span>
                                                    Cascade trigger
                                                    {cascade.bottleneckStation
                                                        ? ` · ${cascade.bottleneckStation}`
                                                        : ""}
                                                </span>
                                                <span className="drive-thru-replay-cascade-meta">
                                                    {showCascadesOnly && (
                                                        <button
                                                            className="drive-thru-replay-cascade-expand-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleGroup(
                                                                    cascade.groupId
                                                                );
                                                            }}
                                                        >
                                                            {getCascadeExpandLabel(
                                                                cascade.cascadeLength,
                                                                expandedGroups.has(
                                                                    cascade.groupId
                                                                )
                                                            )}
                                                        </button>
                                                    )}
                                                </span>
                                            </span>
                                        ) : (
                                            <>
                                                ↑ Cascaded · {cascade.cascadeLength}{" "}
                                                cars
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div
                                className="drive-thru-replay-car-status"
                                data-over-threshold={event.isOverThreshold}
                            >
                                <Icon
                                    name={
                                        event.isOverThreshold
                                            ? "exclamation triangle"
                                            : "check circle"
                                    }
                                    className="drive-thru-replay-car-indicator"
                                    data-over-threshold={event.isOverThreshold}
                                />
                                <div
                                    className="drive-thru-replay-car-duration"
                                    data-over-threshold={event.isOverThreshold}
                                >
                                    {formatDuration(event.totalServiceSeconds)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {showCascadesOnly && hiddenCount > 0 && (
                    <button
                        className="drive-thru-replay-show-all-btn"
                        onClick={() => setViewMode(TimelineViewMode.OccurredWindow)}
                    >
                        Show {hiddenCount} more
                        {peakWindowHours
                            ? ` from ${formatHour(
                                  peakWindowHours.start
                              )}-${formatHour(peakWindowHours.end)}`
                            : " from Peak Window"}
                    </button>
                )}
                {viewMode === TimelineViewMode.OccurredWindow &&
                    allDayHiddenCount > 0 && (
                        <button
                            className="drive-thru-replay-show-all-btn"
                            onClick={() => setViewMode(TimelineViewMode.AllCars)}
                        >
                            Show {allDayHiddenCount} more from All Day
                        </button>
                    )}
            </div>
        </div>
    );
};

export default DriveThruTimeline;
